import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTabsModule } from "@angular/material/tabs";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDividerModule } from "@angular/material/divider";
import { MatCardModule } from "@angular/material/card";
import { CommonModule } from "@angular/common";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";

// Import from centralized model
import { ImageSearchDialogConfig, ImageSearchDialogResult, ImageSearchMethod, MapSelectionRequest } from "../../model";

/**
 * Standalone dialog component for searching by image upload.
 * Uses Material Design Components (MDC) with responsive design.
 *
 * @example
 * ```typescript
 * const dialogRef = SearchImageDialogComponent.open(this.dialog, {
 *   allowedFileTypes: ['image/jpeg', 'image/png'],
 *   maxFileSize: 10485760, // 10MB
 *   enableAdvancedSearch: true
 * });
 *
 * dialogRef.afterClosed().subscribe(result => {
 *   if (result?.success) {
 *     console.log('Image search data:', result.imageData);
 *   }
 * });
 * ```
 */
@Component({
  selector: "app-search-image-dialog",
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDividerModule,
    MatCardModule,
    ReactiveFormsModule,
  ],
  standalone: true,
  templateUrl: "./search-image-dialog.component.html",
  styleUrls: ["./search-image-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchImageDialogComponent implements OnInit {
  // Expose enum to template
  ImageSearchMethod = ImageSearchMethod;

  /**
   * Dialog title
   */
  public title = "Search by image";

  /**
   * Dialog description
   */
  public description = "Choose how you want to provide an image for similarity search.";

  /**
   * Currently selected tab index
   */
  public selectedTabIndex = 0;

  /**
   * URL validation form
   */
  public urlForm: FormGroup;

  /**
   * Currently uploaded file
   */
  public uploadedFile: File | null = null;

  /**
   * Image preview URL (for uploaded files)
   */
  public imagePreview: string | null = null;

  /**
   * WKT data from map selection
   */
  public wktData: string | null = null;

  /**
   * Selected WKT method for map selection
   */
  public selectedWktMethod: "square" | "polygon" | null = null;

  /**
   * Drag and drop state
   */
  public isDragOver = false;

  /**
   * Processing state for search operation
   */
  public isProcessing: boolean = false;

  public globalError: string | null = null;

  /**
   * Validation states for each tab
   */
  public urlValidationState: {
    isValid: boolean;
    isValidating: boolean;
    error?: string;
    previewUrl?: string;
  } = { isValid: false, isValidating: false };

  public uploadValidationState: {
    isValid: boolean;
    error?: string;
    imageData?: {
      base64: string;
      metadata: {
        size: number;
        type: string;
        dimensions?: { width: number; height: number };
      };
    };
  } = { isValid: false };

  public wktValidationState: {
    isValid: boolean;
    isGenerating: boolean;
    base64data?: string;
    error?: string;
    imagePreview?: string;
    geometry?: string;
  } = { isValid: false, isGenerating: false };

  /**
   * Configuration
   */
  private config: ImageSearchDialogConfig;

  /**
   * Default configuration values
   */
  private readonly defaultMaxFileSize = 512000; // 500KB

  private readonly defaultAllowedTypes = ["image/jpeg", "image/png", "image/webp"];

  constructor(
    private dialogRef: MatDialogRef<SearchImageDialogComponent>,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) private dialogData: ImageSearchDialogConfig | null,
  ) {
    this.config = {
      maxFileSize: this.defaultMaxFileSize,
      allowedFileTypes: this.defaultAllowedTypes,
      ...this.dialogData,
    };

    this.urlForm = new FormGroup({
      url: new FormControl("", [Validators.required, Validators.pattern(/^https?:\/\/[^\s$.?#].[^\s]*$/i)]),
    });
  }

  ngOnInit(): void {
    this.initializeFromConfig();
  }

  /**
   * Get current search method based on selected tab
   */
  get currentSearchMethod(): ImageSearchMethod {
    switch (this.selectedTabIndex) {
      case 0:
        return ImageSearchMethod.WKT;
      case 1:
        return ImageSearchMethod.URL;
      case 2:
        return ImageSearchMethod.UPLOAD;
      default:
        return ImageSearchMethod.URL;
    }
  }

  /**
   * Get formatted max file size
   */
  get maxFileSizeFormatted(): string {
    const size = this.config.maxFileSize || this.defaultMaxFileSize;
    return size >= 1024 * 1024 ? `${(size / (1024 * 1024)).toFixed(1)}MB` : `${Math.round(size / 1024)}KB`;
  }

  /**
   * Get accepted file types string
   */
  get acceptedFileTypes(): string {
    return (this.config.allowedFileTypes || this.defaultAllowedTypes).join(",");
  }

  /**
   * Get formatted allowed formats text
   */
  get allowedFormatsText(): string {
    const types = this.config.allowedFileTypes || this.defaultAllowedTypes;
    return types.map((type) => type.split("/")[1].toUpperCase()).join(", ");
  }

  /**
   * Static helper method to open the image search dialog.
   */
  static open(
    dialog: MatDialog,
    config?: ImageSearchDialogConfig,
  ): MatDialogRef<SearchImageDialogComponent, ImageSearchDialogResult> {
    const dialogRef = dialog.open(SearchImageDialogComponent, {
      panelClass: "medium-panel-dialog",
      disableClose: false,
      hasBackdrop: true,
      data: config,
    });

    return dialogRef;
  }

  /**
   * Initialize component from configuration
   */
  private initializeFromConfig(): void {
    if (this.config.title) {
      this.title = this.config.title;
    }

    if (this.config.description) {
      this.description = this.config.description;
    }

    if (this.config.preloadedUrl) {
      this.urlForm.patchValue({ url: this.config.preloadedUrl });
      this.selectedTabIndex = 1;
    }

    if (this.config.wkt) {
      this.wktData = this.config.wkt;
      this.selectedTabIndex = 0;
      this.generateMapPreview(this.wktData, this.config.base64Data);
    }
  }

  /**
   * Handle tab change
   */
  public onTabChange(index: number): void {
    this.selectedTabIndex = index;
    this.clearErrors();
    this.cdr.detectChanges();
  }

  /**
   * Handle drag over event
   */
  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  /**
   * Handle drag leave event
   */
  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const related = event.relatedTarget as Node | null;
    if (related && (event.currentTarget as HTMLElement).contains(related)) {
      return;
    }
    this.isDragOver = false;
  }

  /**
   * Handle drop event
   */
  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  /**
   * Handle file selection from input
   */
  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  /**
   * Process selected file
   */
  private async handleFile(file: File): Promise<void> {
    await this.validateUploadedFile(file);
  }

  /**
   * Remove uploaded file
   */
  public removeFile(event: Event): void {
    event.stopPropagation();
    this.uploadedFile = null;
    this.imagePreview = null;
    this.uploadValidationState = { isValid: false };
    this.cdr.detectChanges();
  }

  /**
   * Format file size for display
   */
  public formatFileSize(bytes: number): string {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    } else if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${bytes} bytes`;
  }

  /**
   * Open map selection (emit special event to close dialog temporarily)
   */
  public openMapSelection(): void {
    // Generate unique request ID for image search
    const requestId = `image-search-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Create map selection request
    const mapSelectionRequest: MapSelectionRequest = {
      id: requestId,
      type: this.selectedWktMethod,
      data: {
        context: "image-search",
        wktMethod: this.selectedWktMethod,
      },
    };

    // Close dialog with map selection request
    this.dialogRef.close({
      success: false,
      searchMethod: ImageSearchMethod.WKT,
      mapSelectionRequest: mapSelectionRequest,
    });
  }

  /**
   * Select WKT method for map selection
   * @param method The selected method
   */
  public selectWktMethod(method: "square" | "polygon"): void {
    this.selectedWktMethod = method;
    this.cdr.detectChanges();
  }

  /**
   * Start WKT selection process
   */
  public startWktSelection(): void {
    if (!this.selectedWktMethod) {
      return;
    }

    this.openMapSelection();
  }

  /**
   * Clear WKT selection
   */
  public clearWktSelection(): void {
    this.wktData = null;
    this.cdr.detectChanges();
  }

  /**
   * Clear errors when switching tabs
   */
  private clearErrors(): void {
    this.globalError = null;
    // Reset validation states
    this.urlValidationState = { isValid: false, isValidating: false };
    this.uploadValidationState = { isValid: false };
    this.wktValidationState = { isValid: false, isGenerating: false };
  }

  /**
   * Validate image URL and generate preview (called automatically on input)
   */
  public async validateImageUrl(): Promise<void> {
    const url = this.urlForm.get("url")?.value?.trim();
    if (!url) {
      this.urlValidationState = { isValid: false, isValidating: false };
      this.cdr.detectChanges();
      return;
    }

    // Only validate if URL format is valid
    if (this.urlForm.get("url")?.errors) {
      this.urlValidationState = { isValid: false, isValidating: false };
      this.cdr.detectChanges();
      return;
    }

    this.urlValidationState = { isValid: false, isValidating: true };
    this.cdr.detectChanges();

    try {
      // Validate URL format
      new URL(url);

      // Test if URL points to a valid image
      const isValidImage = await this.isValidImageUrl(url);
      if (!isValidImage) {
        this.urlValidationState = {
          isValid: false,
          isValidating: false,
          error: "URL does not point to a valid image",
        };
        this.cdr.detectChanges();
        return;
      }

      // Success
      this.urlValidationState = {
        isValid: true,
        isValidating: false,
        previewUrl: url,
      };
    } catch (error) {
      this.urlValidationState = {
        isValid: false,
        isValidating: false,
        error: "Invalid URL format",
      };
    } finally {
      this.cdr.detectChanges();
    }
  }

  /**
   * Validates if a URL points to a valid image
   * Uses a hybrid approach: tries direct load first, then falls back to HEAD request
   */
  private isValidImageUrl(url: string): Promise<boolean> {
    return this.tryDirectImageLoad(url).catch(() => this.tryHeadRequest(url));
  }

  /**
   * Attempt 1: Direct image load (works for CORS-enabled servers)
   */
  private tryDirectImageLoad(url: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const timeout = setTimeout(() => {
        img.onload = img.onerror = null;
        reject(new Error("Timeout"));
      }, 5000);

      img.onload = () => {
        clearTimeout(timeout);
        resolve(true);
      };

      img.onerror = () => {
        clearTimeout(timeout);
        reject(new Error("CORS or invalid image"));
      };

      img.crossOrigin = "anonymous";
      img.src = url;
    });
  }

  /**
   * Attempt 2: Fallback using HEAD request (bypasses CORS client-side)
   */
  private tryHeadRequest(url: string): Promise<boolean> {
    return fetch(url, {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
    })
      .then(() => {
        // With no-cors mode, we can't check status, so we assume success if fetch completes
        return true; // Status 0 means blocked by CORS
      })
      .catch(() => false);
  }

  /**
   * Validate uploaded file and generate preview (called automatically on file selection)
   */
  public async validateUploadedFile(file: File): Promise<void> {
    this.uploadedFile = file;
    this.imagePreview = null;

    try {
      // Check file size
      const maxSize = this.config.maxFileSize || this.defaultMaxFileSize;
      if (file.size > maxSize) {
        this.uploadValidationState = {
          isValid: false,
          error: `File too large. Maximum size is ${this.maxFileSizeFormatted}`,
        };
        this.cdr.detectChanges();
        return;
      }

      // Check file type
      const allowedTypes = this.config.allowedFileTypes || this.defaultAllowedTypes;
      if (!allowedTypes.includes(file.type)) {
        this.uploadValidationState = {
          isValid: false,
          error: `Invalid file type. Allowed formats: ${this.allowedFormatsText}`,
        };
        this.cdr.detectChanges();
        return;
      }

      // Generate preview and validate it's actually an image
      const { preview, dimensions } = await this.generateImagePreviewFromFile(file);

      // Convert to base64 for storage
      const base64 = await this.fileToBase64(file);

      this.imagePreview = preview;
      this.uploadValidationState = {
        isValid: true,
        imageData: {
          base64: base64,
          metadata: {
            size: file.size,
            type: file.type,
            dimensions: dimensions,
          },
        },
      };
    } catch (error) {
      console.error("Error validating uploaded image:", error);
      this.uploadValidationState = {
        isValid: false,
        error: "Invalid image file or processing failed",
      };
    } finally {
      this.cdr.detectChanges();
    }
  }

  /**
   * Generate image preview and extract dimensions
   */
  private generateImagePreviewFromFile(
    file: File,
  ): Promise<{ preview: string; dimensions?: { width: number; height: number } }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.generateImagePreviewFromBase64(e.target?.result as string)
          .then(resolve)
          .catch(reject);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  }

  private generateImagePreviewFromBase64(
    base64: string,
  ): Promise<{ preview: string; dimensions?: { width: number; height: number } }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          preview: this.generatePreviewUrl(img),
          dimensions: { width: img.width, height: img.height },
        });
      };
      img.onerror = () => reject(new Error("Invalid image file"));
      img.src = base64;
    });
  }

  private generatePreviewUrl(image: HTMLImageElement): string {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;

    // Calculate preview size (max 400x400)
    const maxSize = 400;
    let { width, height } = image;

    if (width > height) {
      if (width > maxSize) {
        height = (height * maxSize) / width;
        width = maxSize;
      }
    } else {
      if (height > maxSize) {
        width = (width * maxSize) / height;
        height = maxSize;
      }
    }

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(image, 0, 0, width, height);

    return canvas.toDataURL("image/jpeg", 0.8);
  }

  /**
   * Generate preview from map selection geometry
   */
  public async generateMapPreview(wkt: string, base64data: string): Promise<void> {
    if (!wkt) {
      this.wktValidationState = {
        isValid: false,
        isGenerating: false,
        error: "No geometry provided",
      };
      this.cdr.detectChanges();
      return;
    }

    this.wktValidationState = {
      isValid: false,
      isGenerating: true,
      geometry: wkt,
    };
    this.cdr.detectChanges();

    try {
      if (!base64data) {
        this.wktValidationState = {
          isValid: false,
          isGenerating: false,
          error: "Failed to generate valid preview image from map selection",
          geometry: wkt,
        };
        this.cdr.detectChanges();
        return;
      }

      // const { preview, dimensions } = await this.generateImagePreviewFromBase64(base64data);
      this.wktValidationState = {
        isValid: true,
        isGenerating: false,
        base64data,
        imagePreview: base64data,
        geometry: wkt,
      };
    } catch (error) {
      console.error("Error generating map preview:", error);
      this.wktValidationState = {
        isValid: false,
        isGenerating: false,
        error: "Failed to generate preview from map selection",
        geometry: wkt,
      };
    } finally {
      this.cdr.detectChanges();
    }
  }

  /**
   * Convert file to base64
   */
  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix to get pure base64
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Handle cancel action
   */
  public onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Handle search action
   */
  public async onSearch(): Promise<void> {
    if (!this.canPerformSearch) {
      return;
    }

    this.isProcessing = true;
    this.cdr.detectChanges();

    try {
      let result: ImageSearchDialogResult;

      switch (this.currentSearchMethod) {
        case ImageSearchMethod.URL:
          result = {
            success: true,
            searchMethod: ImageSearchMethod.URL,
            url: this.urlForm.get("url")?.value,
          };
          break;

        case ImageSearchMethod.UPLOAD:
          if (!this.uploadValidationState.imageData) {
            throw new Error("Upload validation data not available");
          }
          result = {
            success: true,
            searchMethod: ImageSearchMethod.UPLOAD,
            base64Data: this.uploadValidationState.imageData.base64,
            imagePreview: this.imagePreview ?? undefined,
          };
          break;

        case ImageSearchMethod.WKT:
          result = {
            success: true,
            searchMethod: ImageSearchMethod.WKT,
            base64Data: this.wktValidationState.base64data,
            imagePreview: this.wktValidationState.imagePreview ?? undefined,
          };
          break;

        default:
          throw new Error("Invalid search method");
      }

      this.dialogRef.close(result);
    } catch (error) {
      console.error("Error processing search:", error);
      // Handle error appropriately
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Whether map area selection is available (based on display mode)
   */
  get canUseMapSelection(): boolean {
    return this.config.canUseMapSelection ?? true; // Default to true for backward compatibility
  }

  /**
   * Get current validation state based on selected tab
   */
  get currentValidationState(): { isValid: boolean; isProcessing: boolean; error?: string } {
    switch (this.selectedTabIndex) {
      case 1: // URL tab
        return {
          isValid: this.urlValidationState.isValid,
          isProcessing: this.urlValidationState.isValidating,
          error: this.urlValidationState.error,
        };
      case 2: // Upload tab
        return {
          isValid: this.uploadValidationState.isValid,
          isProcessing: false,
          error: this.uploadValidationState.error,
        };
      case 0: // WKT/Map tab
        return {
          isValid: this.wktValidationState.isValid,
          isProcessing: this.wktValidationState.isGenerating,
          error: this.wktValidationState.error,
        };
      default:
        return { isValid: false, isProcessing: false };
    }
  }

  /**
   * Check if search can be performed
   */
  get canPerformSearch(): boolean {
    return this.currentValidationState.isValid && !this.currentValidationState.isProcessing;
  }

  /**
   * Check if there's valid content to show in preview section
   */
  get hasValidContent(): boolean {
    return (
      (this.currentSearchMethod === ImageSearchMethod.URL && !!this.urlValidationState?.previewUrl) ||
      (this.currentSearchMethod === ImageSearchMethod.UPLOAD && !!this.imagePreview) ||
      (this.currentSearchMethod === ImageSearchMethod.WKT && !!this.wktValidationState?.imagePreview)
    );
  }
}
